import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star, MessageSquare, X, CheckCircle2 } from "lucide-react";

export default function TestimonialModal({ eventId, eventName, open, onClose }) {
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || !eventId) return;
    setLoaded(false);
    api
      .get(`/events/${eventId}/testimonial`)
      .then((r) => {
        if (r.data) {
          setExisting(r.data);
          setRating(r.data.rating || 5);
          setMessage(r.data.message || "");
        } else {
          setExisting(null);
          setRating(5);
          setMessage("");
        }
      })
      .finally(() => setLoaded(true));
  }, [open, eventId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSaving(true);
    try {
      await api.post(`/events/${eventId}/testimonial`, {
        rating,
        message: message.trim(),
      });
      toast.success("Depoimento enviado! Vou aparecer no site após aprovação.");
      onClose(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => onClose(false)}
      data-testid="testimonial-modal"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="glass rounded-3xl p-8 w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onClose(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-white/10 transition-colors flex items-center justify-center"
          data-testid="testimonial-close"
        >
          <X className="w-4 h-4 text-white/60" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/40 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-[#FF007F]" />
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-white/40">
              Depoimento
            </span>
            <h2 className="font-display font-black text-xl tracking-tight">
              Como foi sua experiência?
            </h2>
          </div>
        </div>

        <p className="text-sm text-white/60 mb-6">
          Compartilha o que achou de usar a Festagram no <b>{eventName}</b>.
          Depois que aprovado, seu depoimento pode aparecer na nossa página.
        </p>

        {existing?.status === "approved" && (
          <div className="mb-4 flex items-center gap-2 text-xs text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-lg px-3 py-2">
            <CheckCircle2 className="w-4 h-4" /> Seu depoimento está aprovado e visível.
          </div>
        )}
        {existing?.status === "pending" && (
          <div className="mb-4 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
            Depoimento aguardando aprovação da equipe Festagram.
          </div>
        )}
        {existing?.status === "rejected" && (
          <div className="mb-4 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            Depoimento anterior não aprovado. Você pode enviar outro abaixo.
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" data-testid="testimonial-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">
              Nota
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                  aria-label={`${n} estrelas`}
                >
                  <Star
                    className={`w-8 h-8 ${
                      n <= rating
                        ? "fill-[#FFD700] text-[#FFD700]"
                        : "text-white/25"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-white/50">{rating}/5</span>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/50 block mb-2">
              Sua mensagem
            </label>
            <textarea
              required
              rows={4}
              maxLength={500}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="input-dark resize-none"
              placeholder="Ex: Meus convidados amaram tirar fotos com a moldura! Chegou um monte de foto linda que eu não teria de outro jeito."
              data-testid="testimonial-message"
            />
            <div className="text-right text-[10px] text-white/40 mt-1">
              {message.length}/500
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || !message.trim()}
            className="btn-neon w-full"
            data-testid="testimonial-submit"
          >
            {saving ? "Enviando..." : existing ? "Atualizar depoimento" : "Enviar depoimento"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
